'use strict';

const requestHelper = require('../helpers/payPalRequestsHelperGlobal');
const helper = require('../../helper');

let btPayPalCheckoutInstancePromise;

/**
 * Push PayPal express click event for GTM.
 * @param {Object} btPayPalConfigurations PayPal configs object
 */
function trackPaypalExpressCartClick(btPayPalConfigurations) {
    const pageType = btPayPalConfigurations
        && btPayPalConfigurations.options
        && btPayPalConfigurations.options.dataAttributes
        && btPayPalConfigurations.options.dataAttributes['page-type'];

    if (pageType !== 'cart') {
        return;
    }

    const payload = {
        event: 'paypalExpress',
        action: 'express checkout'
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
}

/**
 * Function creates PayPal Checkout instance
 * @param {Promise} btClientInstancePromise BT Client Instance Promise
 * @param {Object} payPalPaymentProcessingModal Instance of PayPalPaymentProcessing model. Is needed to have customize CheckoutInstance generation
 * depending on PayPalPaymentProcessing instance configs
 * @returns {Promise} Promise with PayPal Checkout Instance
 */
function createPayPalCheckoutInstance(btClientInstancePromise, payPalPaymentProcessingModal) {
    return btClientInstancePromise
        .then(function(btClientInstance) {
            const payPalCheckoutConfigs = payPalPaymentProcessingModal.payPalCheckoutInstanceConfigs(btClientInstance);

            return braintree.paypalCheckout.create(payPalCheckoutConfigs);
        });
}

/**
 * Creates PayPal button style configs
 * @param {Object} btPayPalConfigurations Configurations of PayPal button
 * @returns {Object} Style configs with the right format
 */
function createPayPalStyleConfigurations(btPayPalConfigurations) {
    const payPalButtonStyleConfigs = btPayPalConfigurations.paypalConfig.style;

    return {
        height: payPalButtonStyleConfigs.height,
        color: payPalButtonStyleConfigs.color,
        shape: payPalButtonStyleConfigs.shape,
        layout: payPalButtonStyleConfigs.layout,
        label: payPalButtonStyleConfigs.label,
        tagline: payPalButtonStyleConfigs.tagline
    };
}

/**
 * Callback which is came from PayPal configurations. Should trigger only "enable" action
 * @param {Object} validateActions Object from Braintree
 */
const payPalFormValidationControl = function(validateActions) {
    const isFormValid = true;

    if (isFormValid) {
        validateActions.enable();
    } else {
        validateActions.disable();
    }
};

const onApprove = function(data, checkoutInstance, paymentProcessingModal) {
    // Some logic here before tokenization happens below
    return checkoutInstance.tokenizePayment(data)
        .then(function(payload) {
            paymentProcessingModal.onApprovePaymentCallback(payload);
        });
};

const createOrder = async function(checkoutInstance, paymentProcessingModal) {
    // Logic which executed after buyer clicked on the button, and PayPal modal start loading
    // "onOrderCreateCallback" is used for forming of order data object
    const paymentObject = await paymentProcessingModal.onOrderCreateCallback();

    return checkoutInstance.createPayment(paymentObject);
};

const validateShippingAddress = async function(data, actions) {
    const requestUrl = `${window.braintreeUrls.validateAddressesUrl}?notFullAddress=true`;
    const response = await requestHelper.validateAddresses(requestUrl, data.shippingAddress);

    if (response.error) {
        await actions.reject(data.errors[response.errorCodes.shift()]);
    }
};

/**
 * Returns PayPal button behavior configs which are used when VaultMode is enabled
 * Is used only on the Account page
 * @param {Promise} btPayPalCheckoutInstance Promise with PayPal Checkout Instance
 * @param {Object} btPayPalConfigurations Object with PayPal configurations
 * @param {Object} payPalPaymentProcessingModal Instance of PayPalPaymentProcessing model
 * @returns {Object} Object with PayPal button configs & callbacks which will handle PayPal button behavior
 */
function getVaultModeButtonConfigurations(btPayPalCheckoutInstance,
                                          btPayPalConfigurations,
                                          payPalPaymentProcessingModal) {
    const payPalButtonStyleConfigs = createPayPalStyleConfigurations(btPayPalConfigurations);

    const paypalButtonConfigurations = {
        fundingSource: window.braintreeConstants.PP_FUNDING_SOURCE_PAYPAL,
        onClick: function(_, actions) {
            trackPaypalExpressCartClick(btPayPalConfigurations);
            return payPalPaymentProcessingModal.onPayPalButtonClickCallback(_, actions);
        },
        createBillingAgreement: function() {
            return createOrder(btPayPalCheckoutInstance, payPalPaymentProcessingModal);
        },
        onApprove: function(data) {
            return onApprove(data, btPayPalCheckoutInstance, payPalPaymentProcessingModal);
        },
        onCancel: function() {
            payPalPaymentProcessingModal.onCancelPaymentCallback();
        },
        onError: function(err) {
            payPalPaymentProcessingModal.onErrorPaymentCallback(err);
        },
        style: payPalButtonStyleConfigs,
        validate: function(validateActions) {
            payPalFormValidationControl(validateActions);
        }
    };

    helper.setButonMessagingConfig(btPayPalConfigurations, paypalButtonConfigurations);

    return paypalButtonConfigurations;
}

/**
 * Returns PayPal button behavior configs which are used when VaultMode is disabled or "Checkout with Vault" is enabled
 * @param {Promise} btPayPalCheckoutInstance Promise with PayPal Checkout Instance
 * @param {Object} btPayPalConfigurations Object with PayPal configurations
 * @param {Object} payPalPaymentProcessingModal Instance of PayPalPaymentProcessing model
 * @returns {Object} Object with PayPal button configs & callbacks which will handle PayPal button behavior
 */
function getCheckoutModeButtonConfigurations(btPayPalCheckoutInstance,
                                             btPayPalConfigurations,
                                             payPalPaymentProcessingModal) {
    const payPalButtonStyleConfigs = createPayPalStyleConfigurations(btPayPalConfigurations);

    const paypalButtonConfigurations = {
        onClick: function(_, actions) {
            trackPaypalExpressCartClick(btPayPalConfigurations);
            return payPalPaymentProcessingModal.onPayPalButtonClickCallback(_, actions);
        },
        createOrder: function() {
            return createOrder(btPayPalCheckoutInstance, payPalPaymentProcessingModal);
        },
        onApprove: function(data) {
            return onApprove(data, btPayPalCheckoutInstance, payPalPaymentProcessingModal);
        },
        onCancel: function() {
            payPalPaymentProcessingModal.onCancelPaymentCallback();
        },
        onError: function(err) {
            payPalPaymentProcessingModal.onErrorPaymentCallback(err);
        },
        style: payPalButtonStyleConfigs,
        validate: function(validateActions) {
            payPalFormValidationControl(validateActions);
        }
    };

    helper.setButonMessagingConfig(btPayPalConfigurations, paypalButtonConfigurations);


    if (payPalPaymentProcessingModal.isExpressCheckout && !payPalPaymentProcessingModal.changePMButtonEnabled) {
        paypalButtonConfigurations.onShippingAddressChange = async function(data, actions) {
            await validateShippingAddress(data, actions);
            await payPalPaymentProcessingModal.onShippingAddressChangeCallback(data, actions, btPayPalCheckoutInstance);
        };

        paypalButtonConfigurations.onShippingOptionsChange = async function(data, actions) {
            await payPalPaymentProcessingModal.onShippingOptionsChangeCallback(data, actions, btPayPalCheckoutInstance);
        };
    }

    return paypalButtonConfigurations;
}

function handleAmountUpdate(pageType) {
    const updateAmount = () => {
        const paypalSDK = window.paypal || window.paypalSdkMiniCart;
        const amount = helper.getPageAmount(pageType);

        if (amount) {
            const btn = paypalSDK.Buttons({
                fundingSource: paypalSDK.FUNDING.PAYPAL
            });

            btn.updateProps({
                message: {
                    amount: amount
                }
            });
        }
    };

    $('body')
        .on('cart:update', updateAmount)
        .on('count:update', updateAmount);
}

/**
 * Checks whether a separate PayPal SDK should be loaded for mini cart
 * @param {Object} paypalButtonSdkUrlConfigs - PayPal button configurations
 * @returns {boolean} - true/false
 */
function shouldMiniCartSdkBeLoaded(paypalButtonSdkUrlConfigs) {
    const pageType = paypalButtonSdkUrlConfigs.dataAttributes && paypalButtonSdkUrlConfigs.dataAttributes['page-type']?.split('-').join('');

    return !window.paypalSdkMiniCart
        && pageType === window.braintreeConstants.PAGE_FLOW_MINICART;
}

/**
 * Function which result is rendered PayPal button
 * @param {Promise} btClientInstancePromise Client instance promise
 * @param {boolean} isVaultMode "true" in case if VaultMode is enabled
 * @param {Object} btPayPalConfigurations Object with PayPal configurations
 * @param {Array} payPalPaymentProcessingModels array of instances for PayPalPaymentProcessing model
 * @returns {Promise} PayPal button
 */
function payPalButtonRendering(btClientInstancePromise,
                               isVaultMode,
                               btPayPalConfigurations,
                               payPalPaymentProcessingModels) {
    // Get ppModel to identify the flow and create sdk config
    const ppPaymentProcessingModel = payPalPaymentProcessingModels.find(Boolean);

    if (!btPayPalCheckoutInstancePromise) {
        // In case if paypal SDK is loaded only for credit messages, we need to delete it
        // and load again to avoid getting unhandled errors in console
        if (window.isPayPalCreditMessageLoaded) {
            delete window.paypal;
        }

        btPayPalCheckoutInstancePromise = createPayPalCheckoutInstance(btClientInstancePromise, ppPaymentProcessingModel);
    }

    function getPayPalButtonConfig(btPayPalCheckoutInstance, btPayPalConfig, payPalPaymentProcessingModel) {
        return isVaultMode
            ? getVaultModeButtonConfigurations(btPayPalCheckoutInstance, btPayPalConfig, payPalPaymentProcessingModel)
            : getCheckoutModeButtonConfigurations(btPayPalCheckoutInstance, btPayPalConfig, payPalPaymentProcessingModel);
    }

    return btPayPalCheckoutInstancePromise
        .then(function(btPayPalCheckoutInstance) {

            const paypalButtonSdkUrlConfigs = isVaultMode
                ? ppPaymentProcessingModel.payPalVaultSdkConfigs()
                : ppPaymentProcessingModel.payPalCheckoutSdkConfigs();

            const shouldLoadMiniCartSDK = shouldMiniCartSdkBeLoaded(paypalButtonSdkUrlConfigs);

            // PayPal button loading
            if (shouldLoadMiniCartSDK || !window.isPayPalSDKLoaded) {
                // We pass params to customize PayPal SDK loading.
                btPayPalCheckoutInstance.loadPayPalSDK(paypalButtonSdkUrlConfigs).then(function() {
                    const paypalSDK = window.paypalSdkMiniCart || window.paypal;

                    window.isPayPalSDKLoaded = true;

                    // Loop through instances and render PP button
                    payPalPaymentProcessingModels.forEach(payPalPaymentProcessingModel => {
                        const paypalButtonConfigs = getPayPalButtonConfig(
                            btPayPalCheckoutInstance,
                            btPayPalConfigurations,
                            payPalPaymentProcessingModel);

                        // eslint-disable-next-line new-cap
                        paypalSDK.Buttons(paypalButtonConfigs).render(payPalPaymentProcessingModel.payPalButtonSelector);

                        helper.hidePayPalBannerIfNotEligible();
                    });
                });
            } else {
                // In case if PayPal SDK was already loaded (with custom parameters), we can simply load PP buttons
                payPalPaymentProcessingModels.forEach(payPalPaymentProcessingModel => {
                    const paypalSDK = window.paypal || window.paypalSdkMiniCart;
                    const paypalButtonConfigs = getPayPalButtonConfig(
                        btPayPalCheckoutInstance,
                        btPayPalConfigurations,
                        payPalPaymentProcessingModel);

                    // eslint-disable-next-line new-cap
                    paypalSDK.Buttons(paypalButtonConfigs).render(payPalPaymentProcessingModel.payPalButtonSelector);
                });
            }

            if (btPayPalConfigurations.btnMessaging) {
                handleAmountUpdate(btPayPalConfigurations.btnMessaging.pageType);
            }
        });
}

module.exports = {
    payPalButtonRendering
};
